/**
 * Query strings out of Sentry's breadcrumbs (BACKLOG AGP-037).
 *
 * Sentry records every fetch, XHR and navigation as a breadcrumb and sends the
 * trail with the next error. A query string can carry a credential: link
 * delivery polls `…/export-status?t=<token>`, a 15-minute credential for one
 * export part. So those breadcrumbs keep their URLs without the query string —
 * the rule `beforeSend` (sentry.ts) already applies to the event's own URL.
 * The path and any `#fragment` stay, for debugging.
 *
 * No imports: Node runs the tests against this file directly
 * (sentryBreadcrumbs.test.mjs).
 */

/** The fields of a Sentry breadcrumb this reads; a Sentry `Breadcrumb` is one. */
export interface BreadcrumbLike {
  category?: string;
  data?: { [key: string]: unknown };
}

/** Which data fields hold a URL, by breadcrumb category. Nothing else is touched. */
const URL_FIELDS = new Map<string, readonly string[]>([
  ['fetch', ['url']],
  ['xhr', ['url']],
  ['navigation', ['from', 'to']],
]);

/**
 * `value` without its query string, keeping any `#fragment`. Plain string
 * work, so a relative path or a URL `new URL` would refuse is cut the same way,
 * at its first `?`, and nothing throws. A `?` inside the fragment is not a
 * query string. Anything that isn't a string comes back as it came.
 */
export function stripQueryString(value: unknown): unknown {
  if (typeof value !== 'string') return value;
  const query = value.indexOf('?');
  if (query === -1) return value;
  const fragment = value.indexOf('#');
  if (fragment !== -1 && fragment < query) return value;
  return value.slice(0, query) + (fragment === -1 ? '' : value.slice(fragment));
}

/**
 * Sentry's `beforeBreadcrumb`. A fetch, XHR or navigation breadcrumb comes back
 * as a copy without query strings in its URLs, or as itself when it has none;
 * every other breadcrumb comes back untouched. Never throws: one that can't be
 * read is dropped (null) rather than kept with a credential in it.
 */
export function scrubBreadcrumb<B extends BreadcrumbLike>(breadcrumb: B): B | null {
  const fields = breadcrumb ? URL_FIELDS.get(breadcrumb.category ?? '') : undefined;
  if (!fields) return breadcrumb;
  try {
    const { data } = breadcrumb;
    if (!data || typeof data !== 'object') return breadcrumb;
    let scrubbed: { [key: string]: unknown } | null = null;
    for (const field of fields) {
      const before = data[field];
      const after = stripQueryString(before);
      if (after !== before) {
        scrubbed ??= { ...data };
        scrubbed[field] = after;
      }
    }
    return scrubbed ? { ...breadcrumb, data: scrubbed } : breadcrumb;
  } catch {
    return null;
  }
}
